import {
  ExpiredColorStatus,
  ExpiredColorStatusId,
  ExpiredColorStatusLabel,
  getStyleRow,
  mapBatchStatusToExpiredColor,
} from '../../../utils/colors'
import { AisleCell, GridCell } from '../types'
import {
  CELL_ALPHA,
  CELL_SURFACE,
  EMPTY_CELL_BG,
  EMPTY_CELL_BG_OCCUPANCY,
  EMPTY_CELL_TEXT,
  HATCH_BACKGROUND,
  MULTI_BRAND_COLOR,
  MapViewMode,
  OCCUPANCY_SCALE,
  UNKNOWN_CATEGORY_COLOR,
  ViewMode,
} from './constants'

// Única diferencia con el vocabulario compartido: 'Vigente' en lugar de 'OK',
// que es el término de `eBatchExpiredStatus` y el que el usuario ve en los
// filtros de Stock.
const BATCH_EXPIRY_LABEL: Record<ExpiredColorStatusId, string> = {
  ...ExpiredColorStatusLabel,
  [ExpiredColorStatus.ok]: 'Vigente',
}

// El estado vigente se pinta sobre un chip y no sobre una fila de tabla: el
// blanco que devuelve `getStyleRow` dejaría el chip sin fondo visible.
const OK_CHIP_BG = '#E8F5E9'

export interface BatchExpiryVisual {
  label: string
  bgcolor: string
  color: string
}

/**
 * Estado de vencimiento de un lote con los colores para pintarlo.
 *
 * Deriva de `getStyleRow`, la misma paleta que usan las filas de Stock,
 * Returns y Stock móvil, para que el chip no se desalinee de ellas.
 */
export function getBatchExpiryVisual(batchState: number): BatchExpiryVisual {
  const status = mapBatchStatusToExpiredColor(batchState)
  const style = getStyleRow(status)

  return {
    label: BATCH_EXPIRY_LABEL[status],
    bgcolor:
      status === ExpiredColorStatus.ok ? OK_CHIP_BG : (style.backgroundColor as string),
    color: style.color as string,
  }
}

const luminanceOf = (r: number, g: number, b: number) =>
  (0.299 * r + 0.587 * g + 0.114 * b) / 255

const channel = (hex: string, i: number) => parseInt(hex.slice(i, i + 2), 16)

export function getContrastText(hex: string): string {
  const luminance = luminanceOf(channel(hex, 1), channel(hex, 3), channel(hex, 5))
  return luminance > 0.55 ? '#212121' : '#FFFFFF'
}

/**
 * Aplica transparencia a un color hex. Deja pasar sin tocar cualquier otro
 * valor —gradientes como el rayado de "sin configurar"— para poder llamarla
 * sobre el fondo de una celda sin averiguar antes de qué tipo es.
 */
export function withAlpha(color: string, alpha: number): string {
  if (!color.startsWith('#') || color.length !== 7) return color
  return `rgba(${channel(color, 1)}, ${channel(color, 3)}, ${channel(color, 5)}, ${alpha})`
}

/**
 * Color de texto legible sobre una celda pintada con transparencia.
 *
 * Mezcla el color con la superficie sobre la que se dibuja antes de medir el
 * contraste: con alpha 0.6 el color que ve el usuario es bastante más claro que
 * el hex original, y medir sobre el hex elegiría texto blanco donde hace falta
 * oscuro. Para fondos no hex (el rayado) devuelve texto oscuro.
 */
export function cellTextColor(bg: string, alpha: number, surface: string): string {
  if (!bg.startsWith('#') || bg.length !== 7) return '#212121'
  const mix = (i: number) => channel(bg, i) * alpha + channel(surface, i) * (1 - alpha)
  return luminanceOf(mix(1), mix(3), mix(5)) > 0.55 ? '#212121' : '#FFFFFF'
}

/** Locale explícito: miles con punto, como el resto de la página. */
export const formatUnits = (value: number) => value.toLocaleString('es-CL')

/**
 * Porcentaje de ocupación con un decimal.
 *
 * Un valor mayor que cero que redondearía a "0,0 %" se muestra como "menor a
 * 0,1 %". Sin ese caso, una posición con 28 unidades sobre 210.840 de capacidad
 * da 0,013 % y aparece como "0,0 %": se lee como vacía cuando no lo está, que
 * es justo la conclusión opuesta a la real.
 */
export function formatOccupancy(pct: number): string {
  // Umbral 0.05: es el valor a partir del cual el redondeo a un decimal deja
  // de dar 0,0.
  if (pct > 0 && pct < 0.05) return 'menor a 0,1 %'

  const value = pct.toLocaleString('es-CL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
  return `${value} %`
}

export const numSort = (a: string, b: string) =>
  String(a).localeCompare(String(b), undefined, { numeric: true })

/**
 * Descarta los valores vacíos de los ejes del mapa.
 *
 * El backend puede devolver un pasillo, nivel o columna en `null` o `''`; sin
 * este filtro se dibuja un encabezado en blanco y una fila entera de celdas
 * fantasma que no corresponden a ninguna posición real.
 */
export const isAxisValue = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim() !== ''

export const formatStock = (n: number): string =>
  n >= 1000 ? `${Math.floor(n / 1000)}K` : String(n)

/** Color del tramo de `OCCUPANCY_SCALE` que contiene a `pct`. */
export const occupancyColor = (pct: number): string =>
  (
    OCCUPANCY_SCALE.find((range) => pct <= range.maxPct) ??
    OCCUPANCY_SCALE[OCCUPANCY_SCALE.length - 1]
  ).color

/** Etiqueta de un tramo de la escala: desde el corte anterior + 1 hasta el suyo. */
export const occupancyRangeLabel = (index: number): string => {
  const from = index === 0 ? 0 : OCCUPANCY_SCALE[index - 1].maxPct + 1
  return `${from} – ${OCCUPANCY_SCALE[index].maxPct}%`
}

export interface CellVisual {
  /** Fondo sin transparencia; puede ser un gradiente en las no configuradas. */
  bg: string
  /** Cifra a mostrar; `null` cuando la posición no está configurada. */
  text: string | null
  /** Color del texto, ya medido contra el fondo con su transparencia aplicada. */
  textColor: string
}

/**
 * Fondo y cifra de una celda del mapa según el modo activo.
 *
 * La grilla y la vista frontal de pasillo pintan con la misma regla; tenerla en
 * un solo lugar evita que una quede con la escala vieja al tocar la otra.
 */
export function resolveCellVisual(
  cell: GridCell | AisleCell,
  mode: ViewMode,
  brandColors: Map<string, string>,
  zoneColors: Map<string, string>
): CellVisual {
  // La cifra es la de la marca filtrada si la hay, el total si no. El color y
  // el porcentaje siguen saliendo de `totalStock` y `occupancyPct`, que el
  // backend no filtra: una celda puede verse naranja por su ocupación real y
  // mostrar 0 porque no tiene la marca buscada.
  const stockText = formatStock(cell.brandStock ?? cell.totalStock)

  const empty = (bg: string): CellVisual => ({ bg, text: '0', textColor: EMPTY_CELL_TEXT })
  const filled = (bg: string): CellVisual => ({
    bg,
    text: stockText,
    textColor: cellTextColor(bg, CELL_ALPHA, CELL_SURFACE),
  })

  if (!cell.isConfigured) {
    return {
      bg: HATCH_BACKGROUND,
      text: null,
      textColor: cellTextColor(HATCH_BACKGROUND, CELL_ALPHA, CELL_SURFACE),
    }
  }

  if (mode === MapViewMode.occupancy) {
    if (cell.totalStock === 0) return empty(EMPTY_CELL_BG_OCCUPANCY)
    return filled(occupancyColor(cell.occupancyPct))
  }

  if (mode === MapViewMode.zone) {
    const zones = cell.zones ?? []
    if (zones.length === 0) return empty(EMPTY_CELL_BG)
    if (cell.isMultiZone) return filled(MULTI_BRAND_COLOR)
    return filled(zoneColors.get(zones[0]) ?? UNKNOWN_CATEGORY_COLOR)
  }

  if (cell.brands.length === 0) return empty(EMPTY_CELL_BG)
  if (cell.isMultiBrand) return filled(MULTI_BRAND_COLOR)
  return filled(brandColors.get(cell.brands[0].name) ?? UNKNOWN_CATEGORY_COLOR)
}
